import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, X } from 'lucide-react';
import { CHECKIN_TEXT_MAX, type CheckinPlace } from '@awwyeah/shared';
import { useMembers } from './members';
import type { PreparedPhoto } from './outbox/image';
import { preparePhoto } from './outbox/image';
import { PlacePin } from './components/PlacePin';
import { useCheckins } from './useCheckins';

/**
 * The composer (design: "Screen F — Composer").
 *
 * Photo first, because that's usually why you opened it. Text is optional and so is the
 * place. The audience line sits next to Post, so "who sees this" is never more than one
 * glance away.
 *
 * Location is **off unless you turn it on, per post**. Turning it on asks the browser for
 * a coordinate once, at that moment, and the *name* is always yours to type — nothing is
 * geocoded, no lookup leaves the device, and the app has no idea where anyone is between
 * check-ins.
 */
export function CheckinComposerPage(): JSX.Element {
  const navigate = useNavigate();
  const { post } = useCheckins();
  const members = useMembers();

  const [photo, setPhoto] = useState<PreparedPhoto | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [text, setText] = useState('');
  const [placeOn, setPlaceOn] = useState(false);
  const [placeName, setPlaceName] = useState('');
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [posting, setPosting] = useState(false);

  const cameraInput = useRef<HTMLInputElement>(null);
  const libraryInput = useRef<HTMLInputElement>(null);

  const selfColor = members.selfId ? members.get(members.selfId).color : 'var(--app-fg-3)';
  const canPost = Boolean(photo || text.trim()) && !posting;
  const remaining = CHECKIN_TEXT_MAX - text.length;

  useEffect(() => {
    return () => {
      if (photo) URL.revokeObjectURL(photo.previewUrl);
    };
  }, [photo]);

  async function onPick(file: File | undefined): Promise<void> {
    if (!file) return;
    setPreparing(true);
    try {
      // Downscale + re-encode (which strips EXIF) before the bytes are ever stored.
      const prepared = await preparePhoto(file);
      setPhoto((previous) => {
        if (previous) URL.revokeObjectURL(previous.previewUrl);
        return prepared;
      });
    } catch (error) {
      console.error('[checkin] could not read that photo', error);
    } finally {
      setPreparing(false);
    }
  }

  function togglePlace(): void {
    if (placeOn) {
      setPlaceOn(false);
      setCoords(null);
      return;
    }
    setPlaceOn(true);
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    setLocating(true);
    // One-shot, at the moment of posting. Never a watch, never a trail.
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 8_000, maximumAge: 60_000 },
    );
  }

  async function submit(): Promise<void> {
    if (!canPost) return;
    setPosting(true);
    const place: CheckinPlace | undefined =
      placeOn && placeName.trim()
        ? { name: placeName.trim(), ...(coords ? { lat: coords.lat, lng: coords.lng } : {}) }
        : undefined;

    try {
      await post({
        ...(text.trim() ? { text: text.trim() } : {}),
        ...(place ? { place } : {}),
        ...(photo ? { photo } : {}),
      });
      navigate('/checkin');
    } catch (error) {
      console.error('[checkin] post failed', error);
      setPosting(false);
    }
  }

  return (
    <div className="-mx-4 flex min-h-[70vh] flex-col" data-testid="checkin-composer">
      <header className="flex h-14 items-center justify-between border-b border-line bg-surface px-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="h-11 px-1 text-[15px] font-semibold text-fg-3"
        >
          Cancel
        </button>
        <span className="text-[17px] font-bold text-fg">Check in</span>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canPost}
          className="h-11 px-1 text-[15px] font-bold text-member-faye disabled:opacity-40"
        >
          Post
        </button>
      </header>

      <div className="flex flex-1 flex-col gap-3 p-4">
        {photo ? (
          <div className="relative flex-shrink-0">
            <img
              src={photo.previewUrl}
              alt="The photo you just took"
              className="max-h-[46vh] w-full border border-line bg-raised object-cover"
            />
            <button
              type="button"
              onClick={() => {
                URL.revokeObjectURL(photo.previewUrl);
                setPhoto(null);
              }}
              aria-label="Remove photo"
              className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full bg-[rgba(18,18,18,0.6)] text-[#F4EFE6]"
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => cameraInput.current?.click()}
            className="flex h-44 flex-shrink-0 flex-col items-center justify-center gap-2 border border-line bg-raised text-fg-3"
            data-testid="checkin-take-photo"
          >
            <Camera size={28} strokeWidth={1.5} />
            <span className="text-sm">{preparing ? 'Getting it ready…' : 'Take a picture'}</span>
          </button>
        )}

        <div className="flex flex-shrink-0 gap-2">
          <button
            type="button"
            onClick={() => cameraInput.current?.click()}
            className="flex h-11 items-center gap-2 rounded-md border-2 border-line-strong px-3.5 text-sm font-semibold text-fg"
          >
            <Camera size={16} strokeWidth={1.75} />
            {photo ? 'Retake' : 'Camera'}
          </button>
          <button
            type="button"
            onClick={() => libraryInput.current?.click()}
            className="flex h-11 items-center rounded-md border border-line px-3.5 text-sm text-fg-2"
          >
            From library
          </button>
        </div>

        {/* Capture goes straight to the rear camera on a phone; the library picker is the
            same input without `capture`, so a laptop gets a plain file dialog. */}
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(event) => void onPick(event.target.files?.[0])}
          data-testid="checkin-camera-input"
        />
        <input
          ref={libraryInput}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => void onPick(event.target.files?.[0])}
          data-testid="checkin-library-input"
        />

        <div className="flex min-h-[104px] flex-col rounded-sm border-2 border-line-strong bg-surface p-3">
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value.slice(0, CHECKIN_TEXT_MAX))}
            placeholder="Say something, or don’t."
            rows={2}
            className="flex-1 resize-none bg-transparent text-base leading-[1.45] text-fg outline-none placeholder:text-fg-3"
            data-testid="checkin-text"
          />
          <span className="mt-auto self-end text-xs text-fg-3">{remaining} left</span>
        </div>

        <div className="flex-shrink-0 overflow-hidden rounded-md border border-line bg-surface">
          <div className="flex min-h-14 items-center gap-3 border-b border-line px-3 py-2">
            <PlacePin color={selfColor} size={40} />
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-semibold text-fg">
                {placeOn ? placeName || 'Name this place' : 'Add a place'}
              </div>
              <div className="text-xs text-fg-3">
                {!placeOn
                  ? 'Off — nothing is recorded'
                  : locating
                    ? 'Asking your phone where you are…'
                    : coords
                      ? 'Where you are right now'
                      : 'No coordinates — the name is enough'}
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={placeOn}
              aria-label="Attach a place to this check-in"
              onClick={togglePlace}
              data-testid="checkin-place-toggle"
              className={`flex h-7 w-12 flex-shrink-0 items-center rounded-full p-0.5 transition-colors duration-150 ease-app ${placeOn ? 'justify-end bg-sync-ok' : 'justify-start bg-line'}`}
            >
              <span className="h-6 w-6 rounded-full bg-[#FBF8F2]" />
            </button>
          </div>
          {placeOn && (
            <div className="flex min-h-12 items-center gap-3 px-3 py-2">
              <input
                value={placeName}
                onChange={(event) => setPlaceName(event.target.value.slice(0, 120))}
                placeholder="Name it something else"
                className="h-9 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-2"
                data-testid="checkin-place-name"
              />
            </div>
          )}
        </div>

        <span className="flex-shrink-0 text-xs leading-[1.45] text-fg-3">
          Location is off unless you turn it on, per post. It’s a place name and a pin, never a live
          trail — the app never knows where you are between check-ins.
        </span>
      </div>

      <div className="sticky bottom-0 flex items-center gap-3 border-t border-line bg-surface px-4 pb-6 pt-2.5">
        <div className="flex-1">
          <div className="text-sm font-semibold text-fg">Goes to the four of you</div>
          <div className="text-xs text-fg-3">Nobody else can see this, ever</div>
        </div>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canPost}
          data-testid="checkin-post-button"
          className="h-13 flex-shrink-0 rounded-md bg-inverse px-6 py-3.5 text-base font-semibold text-inverse-fg disabled:opacity-40"
        >
          Post
        </button>
      </div>
    </div>
  );
}
